import React, { FC } from 'react';
import { range } from 'lodash/fp';
import { Control, Controller, UseFormMethods } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { registerLocale } from 'react-datepicker';
import { enUS, de, fr, it, es, ru } from 'date-fns/locale';

import { CalendarIcon } from 'static';

import { StyledLabel } from '../../styled';
import {
  StyledDateTimeBlock,
  StyledPickerWrapper,
  StyledDatePicker,
  CustomHeader,
} from './styled';
import { SharedStyledDateIcon } from '../styled';

// Register all locales
registerLocale('en', enUS);
registerLocale('de', de);
registerLocale('fr', fr);
registerLocale('it', it);
registerLocale('es', es);
registerLocale('ru', ru);

interface DatePickerWithYearProps
  extends Partial<Pick<UseFormMethods, 'register'>> {
  name: string;
  label: string;
  control: Control;
}

const DatePickerWithYear: FC<DatePickerWithYearProps> = ({
  name,
  register,
  label,
  control,
}) => {
  const { t, i18n } = useTranslation();

  // Get current locale, fallback to 'en' if not supported
  const currentLocale = ['en', 'de', 'fr', 'it', 'es', 'ru'].includes(
    i18n.language,
  )
    ? i18n.language
    : 'en';

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const years = range(2020, 1900, 1);

  // Use translations for month names
  const months = [
    t('common.january'),
    t('common.february'),
    t('common.march'),
    t('common.april'),
    t('common.may'),
    t('common.june'),
    t('common.july'),
    t('common.august'),
    t('common.september'),
    t('common.october'),
    t('common.november'),
    t('common.december'),
  ];

  return (
    <StyledDateTimeBlock>
      <StyledPickerWrapper>
        <StyledLabel htmlFor={name}>{label}</StyledLabel>
        <Controller
          control={control}
          name={name}
          render={({ value, onChange }) => {
            const handleDateChange = (date: Date | null) => {
              if (date) {
                onChange(date);
              } else {
                console.log('cleared');
                onChange(null);
              }
            };

            return (
              <StyledDatePicker
                id={name}
                valueName="selected"
                selected={value}
                dateFormat="dd.MM.yyyy"
                onChange={handleDateChange}
                ref={register}
                name={name}
                placeholderText="-"
                locale={currentLocale}
                calendarStartDay={1}
                renderCustomHeader={({ date, changeYear, changeMonth }) => (
                  <CustomHeader>
                    <select
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      selectedindex={date.getYear()}
                      onChange={({ target: { value } }) => changeYear(value)}
                    >
                      {years.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <select
                      value={months[date.getMonth()]}
                      onChange={({ target: { value } }) =>
                        changeMonth(months.indexOf(value))
                      }
                    >
                      {months.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </CustomHeader>
                )}
              />
            );
          }}
        />
        <SharedStyledDateIcon src={CalendarIcon} alt="" />
      </StyledPickerWrapper>
    </StyledDateTimeBlock>
  );
};

export default DatePickerWithYear;
